'use client';

/**
 * Health Status Indicator
 *
 * Shows a compact indicator of connection health in the toolbar.
 * Helps users quickly see if their "slow" experience is network-related.
 */

import { Activity } from 'lucide-react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:health-status-indicator');
import React, { useEffect, useState } from 'react';
import { getHealthStatusExplanation, runHealthChecks } from '../services/health-check-service';
import { HealthCheckReport, STATUS_MESSAGES } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { Tooltip } from './tooltip';

export const HealthStatusIndicator: React.FC = () => {
  const [healthReport, setHealthReport] = useState<HealthCheckReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  // Run initial health check on mount
  useEffect(() => {
    let isMounted = true; // Track mounted state to prevent updates after unmount

    const runInitialCheck = async () => {
      try {
        const report = await runHealthChecks();
        // Only update state if component is still mounted
        if (isMounted) {
          setHealthReport(report);
        }
      } catch (error) {
        logger.error('[HEALTH-STATUS] Initial check failed:', error);
      } finally {
        // Only update state if component is still mounted
        if (isMounted) {
          setIsInitialLoad(false);
        }
      }
    };

    runInitialCheck();

    // Re-check every 15 minutes (balance between freshness and battery/network usage)
    const interval = setInterval(runInitialCheck, 15 * 60 * 1000);

    return () => {
      isMounted = false; // Mark as unmounted
      clearInterval(interval);
    };
  }, []);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const report = await runHealthChecks();
      setHealthReport(report);
    } catch (error) {
      logger.error('[HEALTH-STATUS] Refresh failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading indicator during initial load
  if (isInitialLoad || !healthReport) {
    return (
      <Tooltip content="Checking connectivity..." side="bottom" delay={300}>
        <div className="h-7 px-2 flex items-center gap-1.5">
          <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
        </div>
      </Tooltip>
    );
  }

  const statusInfo = STATUS_MESSAGES[healthReport.overallStatus];
  const explanation = getHealthStatusExplanation(healthReport);

  // Calculate average latency
  const servicesWithLatency = healthReport.services.filter(s => s.latency);
  const avgLatency =
    servicesWithLatency.length > 0
      ? Math.round(
          healthReport.services.reduce((sum, s) => sum + (s.latency || 0), 0) /
            servicesWithLatency.length
        )
      : 0;

  return (
    <div className="relative">
      <Tooltip content={`${statusInfo.label} • ${avgLatency}ms avg`} side="right" delay={300}>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={cn(
            'h-7 px-2 flex items-center gap-1.5 rounded-md',
            'hover:bg-accent',
            'active:scale-95',
            'transition-all duration-200',
            'text-muted-foreground hover:text-foreground',
            'no-drag' // Prevent dragging window
          )}
          aria-label="Health Status"
        >
          <span className="text-base">{statusInfo.icon}</span>
          {avgLatency > 0 && <span className="text-[10px] font-mono">{avgLatency}ms</span>}
        </button>
      </Tooltip>

      {/* Detailed popup */}
      {showDetails && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setShowDetails(false)} />

          {/* Popup */}
          <div
            className={cn(
              'absolute top-full right-0 mt-2 z-50',
              'w-80 p-4 rounded-2xl',
              'glass-surface-static-solid-bg border border-border/50 shadow-2xl',
              'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{statusInfo.icon}</span>
                <h3 className="text-sm font-semibold text-foreground">{statusInfo.label}</h3>
              </div>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className={cn(
                  'p-1.5 rounded-lg hover:bg-accent transition-colors',
                  isLoading && 'opacity-50 cursor-not-allowed'
                )}
                aria-label="Refresh"
              >
                <Activity className={cn('w-4 h-4', isLoading && 'animate-spin')} />
              </button>
            </div>

            {/* Explanation */}
            <p className="text-xs text-muted-foreground mb-3">{explanation}</p>

            {/* Services */}
            <div className="space-y-2 mb-3">
              {healthReport.services.map(service => {
                const serviceStatus = STATUS_MESSAGES[service.status];
                return (
                  <div key={service.service} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span>{serviceStatus.icon}</span>
                      <span className="text-foreground">{service.serviceName}</span>
                    </div>
                    {service.latency && (
                      <span className="font-mono text-muted-foreground">{service.latency}ms</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Issues */}
            {healthReport.detectedIssues.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-border/30">
                <p className="text-xs font-semibold text-foreground">Detected Issues:</p>
                {healthReport.detectedIssues.map((issue, index) => (
                  <div key={index} className="text-xs space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-foreground flex-1">
                        {issue.severity === 'high'
                          ? '🔴'
                          : issue.severity === 'medium'
                            ? '🟡'
                            : '🟢'}{' '}
                        {issue.description}
                      </p>
                      {issue.confidence && (
                        <span
                          className={cn(
                            'text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide shrink-0',
                            issue.confidence === 'very-high'
                              ? 'bg-red-600/20 text-red-700'
                              : issue.confidence === 'high'
                                ? 'bg-orange-600/20 text-orange-700'
                                : issue.confidence === 'medium'
                                  ? 'bg-yellow-600/20 text-yellow-700'
                                  : 'bg-blue-600/20 text-blue-700'
                          )}
                        >
                          {issue.confidence === 'very-high'
                            ? '95%'
                            : issue.confidence === 'high'
                              ? '85%'
                              : issue.confidence === 'medium'
                                ? '70%'
                                : '50%'}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-[10px] ml-4">💡 {issue.suggestion}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Last checked */}
            <p className="text-[10px] text-muted-foreground/70 text-center mt-3">
              Last checked: {new Date(healthReport.timestamp).toLocaleTimeString()}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthStatusIndicator;
